/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AggregationType } from '@twaice-fe/shared/models';
import {
  Sensor,
  DataPointDE,
  DataPointDEResponse,
  DataPointDEFullResponse,
  SensorDataResponse,
  SensorResponse,
  mapToSensor,
  mapToSensorRequest,
} from '@twaice-fe/frontend/shared/utilities';
import { map, merge, Observable } from 'rxjs';

export interface FetchDataPointsResult {
  dataPoints$: Observable<DataPointDE[]>;
  numChunks: number;
}

@Injectable({
  providedIn: 'root',
})
export class DataExplorerHttpService {
  private readonly WEEK_MS = 7 * 24 * 60 * 60 * 1000;
  private readonly CONCURRENCY = 4;

  constructor(private http: HttpClient) {}

  fetchSensors(customerBk: string, systemBk: string): Observable<Sensor[]> {
    const reqUrl = `data-explorer/customers/${customerBk}/systems/${systemBk}/v2-sensors`;

    return this.http.get<SensorDataResponse>(reqUrl).pipe(
      map((response: SensorDataResponse) => response.data.items),
      map((sensors: SensorResponse[]) => sensors.map((sensor) => mapToSensor(sensor)))
    );
  }

  fetchAllViews(
    customerBk: string,
    systemBk: string
  ): Observable<{ id: string; name: string; description: string; chart_type?: string; isPublic: boolean }[]> {
    const reqUrl = `data-explorer/${customerBk}/${systemBk}/views`;

    return this.http
      .get<{
        data: { views: { view_id: string; name: string; description: string; chart_type?: string; public?: boolean }[] };
      }>(reqUrl)
      .pipe(
        map((response) =>
          response.data.views.map((view) => ({
            id: view.view_id,
            name: view.name,
            description: view.description,
            chart_type: view.chart_type,
            isPublic: view.public ?? false,
          }))
        )
      );
  }

  fetchDataPoints(
    customerBk: string,
    systemBk: string,
    components: string[],
    sensorItems: { aggregationType: AggregationType; sensors: Sensor[] }[],
    startTimestamp: Date,
    endTimestamp: Date,
    samplingMilliseconds: number,
    filteredData: boolean
  ): FetchDataPointsResult {
    const batches: Observable<DataPointDE[]>[] = [];
    const now = new Date();

    sensorItems.forEach((item) => {
      const historicalEnd = endTimestamp <= now ? endTimestamp : now;
      const futureStart = startTimestamp >= now ? startTimestamp : now;

      // Fetch historical data
      if (startTimestamp < now && historicalEnd > startTimestamp) {
        const historicalTimeRangeMs = historicalEnd.getTime() - startTimestamp.getTime();
        if (historicalTimeRangeMs > this.WEEK_MS) {
          batches.push(
            ...this.fetchDataPointsInWeeklyBatches(
              customerBk,
              systemBk,
              components,
              item.sensors,
              startTimestamp,
              historicalEnd,
              samplingMilliseconds,
              item.aggregationType,
              filteredData
            )
          );
        } else {
          const reqUrl = `data-explorer/${customerBk}/${systemBk}/v2-data-points/query`;
          batches.push(
            this.fetchDataPointsInOneBatch(
              customerBk,
              systemBk,
              components,
              item.sensors,
              startTimestamp,
              historicalEnd,
              samplingMilliseconds,
              item.aggregationType,
              reqUrl,
              filteredData
            )
          );
        }
      }

      // Fetch future data
      if (endTimestamp > now && futureStart < endTimestamp) {
        const reqUrl = `data-explorer/${customerBk}/${systemBk}/v2-data-points/query`;
        batches.push(
          this.fetchDataPointsInOneBatch(
            customerBk,
            systemBk,
            components,
            item.sensors,
            futureStart,
            endTimestamp,
            samplingMilliseconds,
            item.aggregationType,
            reqUrl,
            filteredData
          )
        );
      }
    });

    return { dataPoints$: merge(...batches, this.CONCURRENCY), numChunks: batches.length };
  }

  private fetchDataPointsInOneBatch(
    customerBk: string,
    systemBk: string,
    components: string[],
    sensors: Sensor[],
    startTimestamp: Date,
    endTimestamp: Date,
    samplingMilliseconds: number,
    aggregationType: AggregationType,
    reqUrl: string,
    filteredData: boolean
  ): Observable<DataPointDE[]> {
    const payload = {
      component_bks: components,
      sensors: sensors.map(mapToSensorRequest),
      start_timestamp: new Date(startTimestamp.toISOString()),
      end_timestamp: new Date(endTimestamp.toISOString()),
      sampling_seconds: samplingMilliseconds / 1e3,
      aggregation_type: aggregationType,
      filtered_data: filteredData,
    };

    return this.http.post<DataPointDEFullResponse>(reqUrl, payload).pipe(map((res) => this.toDataPoints(res.data.items)));
  }

  private fetchDataPointsInWeeklyBatches(
    customerBk: string,
    systemBk: string,
    components: string[],
    sensors: Sensor[],
    startTimestamp: Date,
    endTimestamp: Date,
    samplingMilliseconds: number,
    aggregationType: AggregationType,
    filteredData: boolean
  ): Observable<DataPointDE[]>[] {
    const batches: Observable<DataPointDE[]>[] = [];
    let currentEnd = new Date(endTimestamp);
    const reqUrl = `data-explorer/${customerBk}/${systemBk}/v2-data-points/query`;

    // Create batches of one week each, starting from the end date
    while (currentEnd > startTimestamp) {
      const batchStart = new Date(Math.max(currentEnd.getTime() - this.WEEK_MS, startTimestamp.getTime()));

      batches.push(
        this.fetchDataPointsInOneBatch(
          customerBk,
          systemBk,
          components,
          sensors,
          batchStart,
          currentEnd,
          samplingMilliseconds,
          aggregationType,
          reqUrl,
          filteredData
        )
      );

      currentEnd = new Date(batchStart.getTime() - 1);
    }

    return batches;
  }

  private toDataPoints(response: DataPointDEResponse[]): DataPointDE[] {
    const result = response.map((dataPointResponse) => ({
      componentBk: dataPointResponse.component_bk,
      sensor: mapToSensor(dataPointResponse.sensor),
      data: dataPointResponse.data,
    }));
    this.sortSensorList(result);
    return result;
  }

  private sortSensorList(datapoints: DataPointDE[]): DataPointDE[] {
    return datapoints.sort((a, b) => {
      // First sort by sensor name
      const nameComparison = a.sensor.name.localeCompare(b.sensor.name);
      if (nameComparison !== 0) return nameComparison;

      // If sensor names are the same, sort by componentBk
      if (a.componentBk < b.componentBk) return -1;
      if (a.componentBk > b.componentBk) return 1;
      return 0;
    });
  }
}
